/**
 * AssetsPage — Raw material workspace list.
 *
 * Shows a grid of the user's projects with name, description, asset count,
 * and status. Includes a "New Project" button that opens a simple modal.
 */

import { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Plus, FolderOpen, Archive, Trash2 } from 'lucide-react'
import apiClient from '../services/api'
import GlobalSearch from '../components/GlobalSearch'
import { apiErrorDetail } from '../utils/dataValidator'

interface Project {
  id: string
  name: string
  description: string | null
  status: string
  created_at: string
  updated_at: string | null
  asset_count: number
}

export default function ProjectsPage() {
  const navigate = useNavigate()
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // New-project modal state
  const [showModal, setShowModal] = useState(false)
  const [newName, setNewName] = useState('')
  const [newDesc, setNewDesc] = useState('')
  const [creating, setCreating] = useState(false)

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true)
      const res = await apiClient.get('/projects/')
      setProjects(res.data.projects)
      setError(null)
    } catch (err) {
      setError(apiErrorDetail(err, 'Failed to load projects'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchProjects() }, [fetchProjects])

  const createProject = async () => {
    if (!newName.trim()) return
    try {
      setCreating(true)
      const res = await apiClient.post('/projects/', {
        name: newName.trim(),
        description: newDesc.trim() || null,
      })
      setShowModal(false)
      setNewName('')
      setNewDesc('')
      const projectId = res.data.id
      navigate(`/projects/${projectId}`)
    } catch (err) {
      setError(apiErrorDetail(err, 'Failed to create project'))
    } finally {
      setCreating(false)
    }
  }

  const deleteProject = async (id: string) => {
    if (!confirm('Delete this project and all its assets?')) return
    try {
      await apiClient.delete(`/projects/${id}`)
      setProjects(prev => prev.filter(p => p.id !== id))
    } catch (err) {
      setError(apiErrorDetail(err, 'Failed to delete project'))
    }
  }

  return (
    <div className="rq-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="rq-title">Assets</h1>
          <p className="rq-subtitle">View and organize source footage used to generate drafts</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="rq-btn-primary px-4 py-2.5"
        >
          <Plus className="w-5 h-5" />
          New Workspace
        </button>
      </div>

      {/* Search */}
      <div className="mb-8 max-w-md">
        <GlobalSearch inline />
      </div>

      {/* Error banner */}
      {error && (
        <div className="bg-red-900/30 border border-red-700 text-red-300 px-4 py-3 rounded-xl mb-6">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="text-center text-[var(--rq-text-soft)] py-20">Loading projects...</div>
      )}

      {/* Empty state */}
      {!loading && projects.length === 0 && (
        <div className="text-center py-20">
          <FolderOpen className="w-16 h-16 text-[var(--rq-text-soft)] mx-auto mb-4" />
          <h2 className="text-lg font-medium text-[var(--rq-text)] mb-2">No assets workspaces yet</h2>
          <p className="text-[var(--rq-text-soft)] mb-6">Create your first workspace to ingest raw material.</p>
          <button
            onClick={() => setShowModal(true)}
            className="rq-btn-primary px-6 py-2.5"
          >
            Create Workspace
          </button>
        </div>
      )}

      {/* Projects grid */}
      {!loading && projects.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map(project => (
            <Link
              key={project.id}
              to={`/projects/${project.id}`}
              className="rq-card rq-card-hover p-6 group"
            >
              <div className="flex items-start gap-3 mb-3">
                <button
                  onClick={(e) => { e.preventDefault(); deleteProject(project.id) }}
                  className="p-1 text-[var(--rq-text-soft)] hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity mt-0.5"
                  title="Delete project"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <FolderOpen className="w-5 h-5 text-[var(--btn)] flex-shrink-0" />
                  <h3 className="text-lg font-semibold text-[var(--rq-text)] group-hover:text-[var(--brand-light)] transition-colors truncate">
                    {project.name}
                  </h3>
                </div>
              </div>
              {project.description && (
                <p className="text-[var(--rq-text-soft)] text-sm mb-4 line-clamp-2">{project.description}</p>
              )}
              <div className="flex items-center justify-between text-xs text-[var(--rq-text-soft)]">
                <span>{project.asset_count} asset{project.asset_count !== 1 ? 's' : ''}</span>
                <span>{new Date(project.created_at).toLocaleDateString()}</span>
              </div>
              {project.status === 'archived' && (
                <span className="mt-3 inline-flex items-center gap-1 text-xs text-amber-400 bg-amber-900/20 px-2 py-0.5 rounded-full">
                  <Archive className="w-3 h-3" /> Archived
                </span>
              )}
            </Link>
          ))}
        </div>
      )}

      {/* New Project Modal */}
      {showModal && (
        <div className="rq-modal-overlay">
          <div className="rq-modal-card">
            <h2 className="text-lg font-bold text-[var(--rq-text)] mb-4">New Workspace</h2>
            <label className="block text-sm text-[var(--rq-text-soft)] mb-1">Name</label>
            <input
              autoFocus
              value={newName}
              onChange={e => setNewName(e.target.value)}
              className="w-full rq-input-dark mb-4"
              placeholder="Monroe Coffee Collective"
              onKeyDown={e => e.key === 'Enter' && createProject()}
            />
            <label className="block text-sm text-[var(--rq-text-soft)] mb-1">Description (optional)</label>
            <textarea
              value={newDesc}
              onChange={e => setNewDesc(e.target.value)}
              className="w-full rq-input-dark mb-6 resize-none"
              rows={3}
              placeholder="Brief description..."
            />
            <div className="flex justify-end gap-3 mt-2">
              <button
                onClick={() => {
                  setShowModal(false)
                  setNewName('')
                  setNewDesc('')
                }}
                className="rq-btn-ghost px-4 py-2"
              >
                Cancel
              </button>
              <button
                onClick={createProject}
                disabled={creating || !newName.trim()}
                className="rq-btn-primary px-5 py-2 disabled:opacity-50"
              >
                {creating ? 'Creating...' : 'Create Workspace'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
